import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams, Link } from "react-router-dom";
import Barcode from "react-barcode";
import { api } from "../lib/api";
import { Printer, ArrowLeft, Settings as Cog } from "lucide-react";
import { toast } from "sonner";

const DEFAULT_SETTINGS = {
  width_mm: 100, height_mm: 50, font_scale: 0.8,
  show_barcode: true, show_qty: true, show_rack: true,
  show_lot: true, show_size: true, show_color: true, show_category: true,
  label_footer: "",
};

const LABEL_PRESETS = [
  { label: "25 × 50 mm", w: 25, h: 50, scale: 0.5 },
  { label: "50 × 50 mm", w: 50, h: 50, scale: 0.7 },
  { label: "100 × 50 mm (default)", w: 100, h: 50, scale: 1.0 },
  { label: "100 × 100 mm", w: 100, h: 100, scale: 1.2 },
  { label: "100 × 200 mm", w: 100, h: 200, scale: 1.6 },
];

export default function Labels() {
  const [params] = useSearchParams();
  const lotId = params.get("lot");
  const countParam = params.get("count");
  const [data, setData] = useState(null);
  const [count, setCount] = useState(countParam ? parseInt(countParam,10) : null);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [lotChoices, setLotChoices] = useState([]);
  const [showSettings, setShowSettings] = useState(false);

  useEffect(() => {
    api.get("/label-settings").then(r => setSettings({ ...DEFAULT_SETTINGS, ...r.data })).catch(() => {});
  }, []);

  useEffect(() => {
    if (lotId) {
      const p = countParam ? { count: parseInt(countParam,10) } : {};
      api.get(`/lots/${lotId}/labels`, { params: p }).then(r => {
        setData(r.data);
        setCount(r.data.count);
      });
    } else {
      api.get("/lots").then(r => setLotChoices(r.data));
    }
  }, [lotId, countParam]);

  const saveSettings = async () => {
    try {
      await api.put("/label-settings", settings);
      toast.success("Label settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    }
  };

  if (!lotId) {
    return (
      <div className="space-y-6" data-testid="labels-picker">
        <header>
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-2">Thermal Labels</div>
          <h1 className="font-display font-bold text-4xl tracking-tight text-[#2D2622]">Pick a lot to print.</h1>
          <p className="text-[#5D5148] mt-2 text-sm">Each label prints at {settings.width_mm}×{settings.height_mm} mm with barcode.</p>
        </header>
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {lotChoices.map(l => (
            <Link to={`/labels?lot=${l.id}`} key={l.id}
              className="p-4 bg-white border border-[#E5D8C4] hover:border-[#2D2622]">
              <div className="flex justify-between items-start">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#8B6F47] font-semibold">{l.category_name}</div>
                  <div className="font-display font-semibold mt-1 text-[#2D2622]">{l.color_name}</div>
                  <div className="font-mono2 text-xs text-[#8B6F47] mt-1">{l.size_label}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono2 text-2xl font-bold text-[#2D2622]">{l.quantity}</div>
                  <div className="text-[10px] uppercase tracking-widest text-[#8B6F47]">pcs</div>
                </div>
              </div>
              <div className="mt-3 pt-3 border-t border-[#F3ECDF] flex justify-between text-[11px] font-mono2 text-[#5D5148]">
                <span>Lot {l.lot_no}</span><span>Rack {l.rack_no}</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    );
  }

  if (!data) return <div className="text-[#A8998A] text-sm">Loading…</div>;

  const { lot, barcode } = data;
  const labels = Array.from({ length: count });

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-start no-print flex-wrap gap-3">
        <div className="min-w-0 flex-1">
          <Link to="/stock" className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-[#8B6F47] hover:text-[#2D2622]">
            <ArrowLeft size={14}/> Back
          </Link>
          <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight text-[#2D2622] mt-3 break-words">
            Print labels — {lot.color_name}
          </h1>
          <div className="text-[#8B6F47] text-xs sm:text-sm mt-1 font-mono2 break-words">
            {lot.category_name} · {lot.size_label} · Lot {lot.lot_no} · Rack {lot.rack_no}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
          <button onClick={() => setShowSettings(s => !s)}
            className="h-11 px-3 sm:px-4 border border-[#E5D8C4] bg-white text-[11px] sm:text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:border-[#2D2622]">
            <Cog size={14}/> <span className="hidden sm:inline">Label</span> settings
          </button>
          <div className="text-[10px] uppercase tracking-widest text-[#8B6F47]">Copies</div>
          <input type="number" min="1" value={count} data-testid="label-count"
            onChange={(e) => setCount(Math.max(1, parseInt(e.target.value || "1", 10)))}
            className="h-11 w-16 sm:w-20 px-2 border border-[#E5D8C4] font-mono2 text-center focus:outline-none focus:border-[#2D2622]" />
          <button onClick={() => window.print()} data-testid="print-btn"
            className="h-11 px-4 sm:px-5 bg-[#2D2622] text-white text-[11px] sm:text-xs uppercase tracking-[0.2em] inline-flex items-center gap-2 hover:bg-[#5D4A2E]">
            <Printer size={14}/> Print {count}
          </button>
        </div>
      </div>

      {showSettings && (
        <div className="bg-white border border-[#E5D8C4] p-5 no-print">
          <div className="text-[11px] uppercase tracking-[0.3em] text-[#8B6F47] font-semibold mb-3">Customize labels</div>
          <div className="mb-3">
            <label className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47] font-semibold mb-1 block">Preset size</label>
            <select data-testid="label-preset"
              onChange={(e) => {
                const p = LABEL_PRESETS[parseInt(e.target.value, 10)];
                if (p) setSettings(s => ({ ...s, width_mm: p.w, height_mm: p.h, font_scale: p.scale }));
              }}
              className="h-10 px-3 border border-[#E5D8C4] text-sm bg-white w-full md:max-w-xs">
              <option value="">Choose preset…</option>
              {LABEL_PRESETS.map((p, i) => <option key={p.label} value={i}>{p.label}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <NumField label="Width (mm)" value={settings.width_mm}
              onChange={(v) => setSettings({ ...settings, width_mm: v })} />
            <NumField label="Height (mm)" value={settings.height_mm}
              onChange={(v) => setSettings({ ...settings, height_mm: v })} />
            <NumField label="Font scale" step="0.1" value={settings.font_scale}
              onChange={(v) => setSettings({ ...settings, font_scale: v })} />
            <TextField label="Footer text" value={settings.label_footer || ""}
              onChange={(v) => setSettings({ ...settings, label_footer: v })} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-3">
            {[
              ["show_barcode", "Barcode"], ["show_qty", "Quantity"],
              ["show_rack", "Rack"], ["show_lot", "Lot"],
              ["show_size", "Size"], ["show_color", "Colour"], ["show_category", "Category"]
            ].map(([k, label]) => (
              <label key={k} className={`flex items-center gap-2 p-2 border text-xs cursor-pointer ${settings[k]?"border-[#2D2622] bg-[#F3ECDF]":"border-[#E5D8C4]"}`}>
                <input type="checkbox" checked={settings[k]}
                  onChange={(e) => setSettings({ ...settings, [k]: e.target.checked })} />
                {label}
              </label>
            ))}
          </div>
          <div className="mt-4 flex justify-end">
            <button onClick={saveSettings}
              className="h-9 px-4 bg-[#2D2622] text-white text-xs uppercase tracking-[0.2em]">Save settings</button>
          </div>
        </div>
      )}

      <div className="bg-[#F3ECDF] p-4 sm:p-6 border border-[#E5D8C4] no-print flex flex-col items-center">
        <div className="text-[11px] uppercase tracking-widest text-[#8B6F47] mb-3 font-semibold self-start">
          Preview ({settings.width_mm} × {settings.height_mm} mm)
        </div>
        <div className="bg-white shadow-sm max-w-full overflow-x-auto">
          <LabelTemplate lot={lot} barcode={barcode} settings={settings} isPreview />
        </div>
      </div>

      {/* Print portal — rendered directly under <body> so we can hide every
          other node with a single CSS rule. */}
      {createPortal(
        <div id="vs-print-portal">
          <style>{`
            /* Enforce a physical 100mm × 50mm label page (landscape by virtue
               of width > height). Overridable via settings but the CSS var
               keeps thermal-driver interpretation predictable. */
            @page {
              size: ${settings.width_mm}mm ${settings.height_mm}mm;
              margin: 0;
            }
            @media print {
              html, body {
                margin: 0 !important;
                padding: 0 !important;
                background: #fff !important;
                width: ${settings.width_mm}mm !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              body > *:not(#vs-print-portal) { display: none !important; }
              #vs-print-portal {
                display: block !important;
                position: static !important;
                margin: 0 !important;
                padding: 0 !important;
                width: ${settings.width_mm}mm !important;
                background: #fff !important;
              }
              /* Label text + generic descendants stay solid black on white.
                 We deliberately do NOT touch <svg> children here — the barcode
                 SVG carries its own white background rect and black bar rects,
                 and blanket-forcing fill:#000 on every child turned the barcode
                 into a solid black rectangle. */
              #vs-print-portal .thermal-label,
              #vs-print-portal .thermal-label div,
              #vs-print-portal .thermal-label span,
              #vs-print-portal .thermal-label p,
              #vs-print-portal .thermal-label td,
              #vs-print-portal .thermal-label th {
                visibility: visible !important;
                opacity: 1 !important;
                color: #000 !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
              }
              /* Barcode wrapper: white background, no inherited dark bg from
                 the app shell; SVG itself keeps its native fills. */
              #vs-print-portal .thermal-label .vs-barcode {
                background: #fff !important;
                padding: 0 !important;
                margin: 0 !important;
                display: flex !important;
                justify-content: center !important;
                visibility: visible !important;
                opacity: 1 !important;
              }
              /* SVG needs explicit visibility + print-color-adjust — Chrome
                 drops inherited visibility on <svg> during print, and drops
                 non-CSS fill attributes without an explicit color-adjust. */
              #vs-print-portal .thermal-label .vs-barcode svg,
              #vs-print-portal .thermal-label .vs-barcode svg rect,
              #vs-print-portal .thermal-label .vs-barcode svg text,
              #vs-print-portal .thermal-label .vs-barcode svg path,
              #vs-print-portal .thermal-label .vs-barcode svg g {
                visibility: visible !important;
                opacity: 1 !important;
                display: initial !important;
                -webkit-print-color-adjust: exact !important;
                print-color-adjust: exact !important;
                color-adjust: exact !important;
              }
              #vs-print-portal .thermal-label .vs-barcode svg {
                background: #fff !important;
                max-width: 100% !important;
                height: auto !important;
              }
              /* Explicitly DO NOT set fill/stroke on svg * — react-barcode
                 already draws white background rect + black bar rects with the
                 right colours. */
              #vs-print-portal .thermal-label {
                width: ${settings.width_mm}mm !important;
                height: ${settings.height_mm}mm !important;
                max-height: ${settings.height_mm}mm !important;
                overflow: hidden !important;
                margin: 0 !important;
                padding: 1.5mm !important;
                box-sizing: border-box !important;
                display: flex !important;
                flex-direction: column !important;
                background: #fff !important;
              }
              #vs-print-portal .thermal-label:not(:last-child) {
                page-break-after: always !important;
                break-after: page !important;
              }
              #vs-print-portal .thermal-label:last-child {
                page-break-after: avoid !important;
                break-after: avoid !important;
              }
              #vs-print-portal .thermal-label {
                page-break-inside: avoid !important;
                break-inside: avoid !important;
              }
            }
          `}</style>
          {labels.map((_, i) => (
            <LabelTemplate key={`print-${i}`} lot={lot} barcode={barcode} settings={settings} />
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function LabelTemplate({ lot, barcode, settings, isPreview }) {
  const style = {
    width: `${settings.width_mm}mm`,
    height: `${settings.height_mm}mm`,
    fontSize: `${8 * settings.font_scale}pt`,
    padding: "1.5mm",
    boxSizing: "border-box",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
  };
  return (
    <div className={`thermal-label ${isPreview ? "thermal-preview" : ""}`} style={style}>
      <div style={{fontSize:`${10 * settings.font_scale}pt`, lineHeight:1.4, flex:1, fontWeight:700}}>
        {settings.show_color && <Row scale={settings.font_scale} k="COLOUR" v={lot.color_name} />}
        {settings.show_size  && <Row scale={settings.font_scale} k="SIZE"   v={lot.size_label} />}
        {settings.show_lot   && <Row scale={settings.font_scale} k="LOT"    v={lot.lot_no} />}
        {settings.show_qty   && <Row scale={settings.font_scale} k="QTY"    v={`${lot.quantity} PCS`} />}
        {settings.show_rack  && <Row scale={settings.font_scale} k="RACK"   v={lot.rack_no} />}
      </div>
      {settings.show_barcode && (lot.barcode_on_label !== false) && (
        <div className="vs-barcode" style={{marginTop:"1mm", display:"flex", justifyContent:"center",
                     background:"#fff"}}>
          <Barcode value={barcode} height={30 * settings.font_scale}
                   width={1.5 * settings.font_scale}
                   fontSize={10 * settings.font_scale} margin={0} displayValue
                   background="#ffffff" lineColor="#000000" />
        </div>
      )}
      {settings.label_footer && (
        <div style={{textAlign:"center", fontSize:`${7 * settings.font_scale}pt`, marginTop:"0.5mm"}}>
          {settings.label_footer}
        </div>
      )}
    </div>
  );
}

function Row({ k, v, scale = 1 }) {
  return (
    <div style={{display:"flex", justifyContent:"space-between", gap:"3mm",
                 padding:"0.5mm 0", borderBottom:"0.3pt dashed #999"}}>
      <div style={{fontWeight:700, color:"#333", fontSize:`${9 * scale}pt`}}>{k}</div>
      <div style={{fontWeight:900, textAlign:"right", fontSize:`${11 * scale}pt`,
                   maxWidth:"70%", overflow:"hidden", textOverflow:"ellipsis"}}>{v}</div>
    </div>
  );
}

function NumField({ label, value, onChange, step = "1" }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47] font-semibold mb-1 block">{label}</label>
      <input type="number" step={step} value={value}
        onChange={(e) => onChange(parseFloat(e.target.value || "0"))}
        className="h-9 px-2 border border-[#E5D8C4] w-full font-mono2 text-sm focus:outline-none focus:border-[#2D2622]" />
    </div>
  );
}

function TextField({ label, value, onChange }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-[#8B6F47] font-semibold mb-1 block">{label}</label>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="h-9 px-2 border border-[#E5D8C4] w-full text-sm focus:outline-none focus:border-[#2D2622]" />
    </div>
  );
}
